import { forwardRef } from "react";

import Barcode from "react-barcode";

import dayjs from "dayjs";

import type { ProductionBatch } from "~/types/production";
import { generateBatchQrCode } from "~/utils/barcodeParser";

interface BatchLabelPrintProps {
  batch: ProductionBatch;
  labelCount: number;
  elaborationDate?: string;
}

interface BatchLabelProps {
  batch: ProductionBatch;
  elaborationDate?: string;
}

/**
 * Single batch label component
 */
function BatchLabel({ batch, elaborationDate }: BatchLabelProps) {
  const qrValue = generateBatchQrCode(batch.batch_number);
  const productName =
    batch.product?.short_name || batch.product?.name || "Producto";
  const expirationDate = batch.expiration_date
    ? dayjs(batch.expiration_date).format("DD/MM/YYYY")
    : "N/A";
  const formattedElaborationDate = elaborationDate
    ? dayjs(elaborationDate).format("DD/MM/YYYY")
    : dayjs().format("DD/MM/YYYY");

  return (
    <div className="batch-label">
      {/* Left side: Title, batch number, barcode */}
      <div className="label-left">
        <div className="label-header">
          <span className="label-title">LOTE DE PRODUCCIÓN</span>
        </div>

        <div className="label-batch-number">{batch.batch_number}</div>

        <div className="barcode-container">
          <Barcode
            value={qrValue}
            format="CODE128"
            width={1.5}
            height={50}
            displayValue={false}
            margin={0}
          />
        </div>

        <div className="label-footer">
          <span className="barcode-value">{qrValue}</span>
        </div>
      </div>

      {/* Right side: Product details */}
      <div className="label-right">
        <div className="label-details">
          <div className="detail-row">
            <span className="detail-label">Producto:</span>
            <span className="detail-value">{productName}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Cantidad:</span>
            <span className="detail-value">
              {batch.actual_quantity || batch.target_quantity} cajas
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Elaboración:</span>
            <span className="detail-value">{formattedElaborationDate}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Vencimiento:</span>
            <span className="detail-value">{expirationDate}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Printable component that renders multiple batch labels per page
 * Uses @media print CSS for proper formatting
 * Optimized for 101mm x 175mm sticker paper
 */
export const BatchLabelPrint = forwardRef<HTMLDivElement, BatchLabelPrintProps>(
  function BatchLabelPrint({ batch, labelCount, elaborationDate }, ref) {
    // Create array of labels to render
    const labels = Array.from({ length: labelCount }, (_, i) => i);

    return (
      <div ref={ref} className="print-container">
        <style>{`
          /* Screen styles - preview (horizontal layout) */
          .print-container {
            padding: 20px;
            background: white;
          }

          .labels-grid {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }

          .batch-label {
            border: 2px dashed #ccc;
            border-radius: 8px;
            padding: 16px;
            background: white;
            width: 480px;
            box-sizing: border-box;
            overflow: hidden;
            display: flex;
            flex-direction: row;
            gap: 16px;
          }

          .label-left {
            flex: 1;
            display: flex;
            flex-direction: column;
          }

          .label-right {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }

          .label-header {
            text-align: center;
            border-bottom: 1px solid #e5e5e5;
            padding-bottom: 8px;
            margin-bottom: 8px;
          }

          .label-title {
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.5px;
            color: #666;
          }

          .label-batch-number {
            font-size: 16px;
            font-weight: 700;
            text-align: center;
            margin-bottom: 8px;
            color: #111;
          }

          .barcode-container {
            display: flex;
            justify-content: center;
            align-items: center;
            flex-grow: 1;
            overflow: hidden;
          }

          .barcode-container svg {
            max-width: 100%;
            height: auto;
          }

          .label-footer {
            margin-top: 8px;
            padding-top: 8px;
            border-top: 1px solid #e5e5e5;
            text-align: center;
          }

          .barcode-value {
            font-size: 9px;
            color: #999;
            font-family: monospace;
          }

          .label-details {
            font-size: 11px;
          }

          .detail-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 6px;
            padding-bottom: 6px;
            border-bottom: 1px solid #e5e5e5;
          }

          .detail-row:last-child {
            border-bottom: none;
            margin-bottom: 0;
            padding-bottom: 0;
          }

          .detail-label {
            font-weight: 500;
            color: #666;
          }

          .detail-value {
            color: #111;
            text-align: right;
            font-weight: 600;
          }

          /* Print styles - horizontal 175mm x 101mm sticker */
          @media print {
            @page {
              size: 175mm 101mm;
              margin: 5mm;
            }

            .print-container {
              padding: 0;
            }

            .labels-grid {
              display: block;
            }

            .batch-label {
              border: 1px solid #000;
              border-radius: 4px;
              padding: 6mm;
              page-break-after: always;
              width: 165mm;
              height: 91mm;
              box-sizing: border-box;
              display: flex;
              flex-direction: row;
              gap: 6mm;
            }

            .label-left {
              flex: 1;
              display: flex;
              flex-direction: column;
              border-right: 1px solid #000;
              padding-right: 6mm;
            }

            .label-right {
              flex: 1;
              display: flex;
              flex-direction: column;
              justify-content: center;
            }

            .label-header {
              border-bottom: 1px solid #000;
              padding-bottom: 3mm;
              margin-bottom: 3mm;
            }

            .label-title {
              font-size: 12pt;
              color: #000;
            }

            .label-batch-number {
              font-size: 18pt;
              color: #000;
              margin-bottom: 4mm;
            }

            .barcode-container {
              flex-grow: 1;
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .barcode-container svg {
              width: 70mm !important;
              height: 20mm !important;
            }

            .label-footer {
              border-top: 1px solid #000;
              padding-top: 3mm;
              margin-top: 3mm;
            }

            .barcode-value {
              font-size: 9pt;
              color: #333;
            }

            .label-details {
              font-size: 12pt;
            }

            .detail-row {
              margin-bottom: 4mm;
              padding-bottom: 4mm;
              border-bottom: 1px solid #ccc;
            }

            .detail-row:last-child {
              border-bottom: none;
            }

            .detail-label,
            .detail-value {
              color: #000;
            }
          }
        `}</style>

        <div className="labels-grid">
          {labels.map((index) => (
            <BatchLabel
              key={index}
              batch={batch}
              elaborationDate={elaborationDate}
            />
          ))}
        </div>
      </div>
    );
  }
);

export default BatchLabelPrint;
